import React, { useState } from 'react';
import { MERIDIANS, compute, CandidateResult } from '../data/matrixData';
import { EmptyState } from './EmptyState';
import { MatrixResult } from './MatrixResult';
export function MatrixCalculator() {
  const [selectedMeridians, setSelectedMeridians] = useState<string[]>([]);
  const [result, setResult] = useState<CandidateResult[] | null>(null);
  const toggleMeridian = (meridian: string) => {
    setSelectedMeridians(prev => {
      const newSelection = prev.includes(meridian) ? prev.filter(m => m !== meridian) : [...prev, meridian];
      setResult(null);
      return newSelection;
    });
  };
  const handleCalculate = () => {
    if (selectedMeridians.length >= 2) {
      setResult(compute(selectedMeridians));
    }
  };
  const handleClear = () => {
    setSelectedMeridians([]);
    setResult(null);
  };
  return <div className="flex flex-col gap-6 md:grid md:grid-cols-[340px_1fr] md:gap-7 md:items-start animate-fade" data-id="element-61">
      {/* Left Panel */}
      <div className="md:sticky md:top-6 bg-bg1 border border-border rounded-[10px] overflow-hidden shadow-md" data-id="element-62">
        <div className="py-4 px-4 md:px-5 border-b border-border" data-id="element-63">
          <div className="font-sans text-[0.7rem] font-extrabold tracking-[0.1em] uppercase text-teal mb-3 flex items-center gap-2 after:content-[''] after:flex-1 after:h-px after:bg-border" data-id="element-64">
            Select Sick Meridians
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5" data-id="element-65">
            {MERIDIANS.map(m => {
            const isSelected = selectedMeridians.includes(m.a);
            return <button key={m.a} onClick={() => toggleMeridian(m.a)} className={`relative py-2 px-1 rounded-md border-[1.5px] font-sans text-[0.72rem] font-bold cursor-pointer text-center leading-[1.2] transition-colors duration-150 ${isSelected ? 'bg-teal-p border-teal-l text-teal' : 'bg-white border-border text-ink3 hover:border-teal-l hover:text-teal hover:bg-teal-p'}`} data-id="element-66">
                  <span className="text-[0.82rem] block font-extrabold" data-id="element-67">
                    {m.a}
                  </span>
                  <span className={`text-[0.46rem] font-normal block mt-px leading-[1.3] ${isSelected ? 'text-teal' : 'text-muted'}`} data-id="element-68">
                    {m.n}
                  </span>
                  {isSelected && <span className="absolute top-0.5 right-1 text-[0.44rem] text-teal-l font-black" data-id="element-69">
                      ✓
                    </span>}
                </button>;
          })}
          </div>
        </div>

        <div className="py-4 px-4 md:px-5 border-b border-border" data-id="element-70">
          <div className="flex items-center justify-between mb-2.5" data-id="element-71">
            <div data-id="element-72">
              <div className="font-sans text-[1.7rem] font-extrabold text-teal leading-none" data-id="element-73">
                {selectedMeridians.length}
              </div>
              <div className="text-[0.6rem] text-muted mt-0.5" data-id="element-74">
                meridians selected
              </div>
            </div>
            <span className="text-[0.66rem] text-muted italic" data-id="element-75">
              Click chip to remove
            </span>
          </div>

          <div className="flex flex-wrap gap-1.5 min-h-[30px] items-start" data-id="element-76">
            {selectedMeridians.length === 0 ? <span className="text-[0.74rem] text-muted italic py-1" data-id="element-77">
                No meridians selected yet
              </span> : selectedMeridians.map(m => <span key={m} onClick={() => toggleMeridian(m)} className="inline-flex items-center gap-1.5 py-1 px-2.5 rounded-full text-[0.72rem] font-bold bg-teal-p text-teal border border-teal/30 cursor-pointer transition-colors duration-150 hover:bg-red-p hover:text-red hover:border-red/30" data-id="element-78">
                  {m} <span className="text-[0.58rem] opacity-65" data-id="element-79">✕</span>
                </span>)}
          </div>
        </div>

        <div className="py-4 px-4 md:px-5 border-b border-border" data-id="element-80">
          <button onClick={handleCalculate} disabled={selectedMeridians.length < 2} className="w-full p-3 mb-2 bg-gold-d text-white border-none rounded-md font-sans text-[0.84rem] font-bold tracking-[0.07em] uppercase cursor-pointer transition-all duration-200 shadow-teal-btn hover:-translate-y-px hover:bg-amber hover:shadow-teal-btn-hover disabled:bg-bg3 disabled:text-muted disabled:cursor-not-allowed disabled:hover:transform-none disabled:hover:shadow-none" data-id="element-81">
            Find Common Meridian
          </button>

          <button onClick={handleClear} className="w-full p-2 bg-transparent text-ink3 border border-border rounded-md text-[0.74rem] cursor-pointer transition-colors duration-150 hover:text-teal hover:border-teal-l" data-id="element-82">
            ↻ Clear All
          </button>
        </div>

        <div className="py-4 px-4 md:px-5" data-id="element-83">
          <div className="font-sans text-[0.7rem] font-extrabold tracking-[0.1em] uppercase text-teal mb-3 flex items-center gap-2 after:content-[''] after:flex-1 after:h-px after:bg-border" data-id="element-84">
            How It Works
          </div>
          <div className="text-[0.75rem] text-ink3 leading-[1.6]" data-id="element-85">
            <div className="flex gap-2 items-start mb-2" data-id="element-86">
              <div className="w-[17px] h-[17px] rounded-full bg-teal text-white text-[0.56rem] font-extrabold flex items-center justify-center shrink-0 mt-0.5" data-id="element-87">
                1
              </div>
              <div data-id="element-88">
                Select <b className="text-teal font-bold" data-id="element-89">2 or more</b> sick
                meridians.
              </div>
            </div>
            <div className="flex gap-2 items-start mb-2" data-id="element-90">
              <div className="w-[17px] h-[17px] rounded-full bg-teal text-white text-[0.56rem] font-extrabold flex items-center justify-center shrink-0 mt-0.5" data-id="element-91">
                2
              </div>
              <div data-id="element-92">
                The finder searches all 5 balancing systems for every meridian
                that can balance at least one of your selections.
              </div>
            </div>
            <div className="flex gap-2 items-start mb-2" data-id="element-93">
              <div className="w-[17px] h-[17px] rounded-full bg-teal text-white text-[0.56rem] font-extrabold flex items-center justify-center shrink-0 mt-0.5" data-id="element-94">
                3
              </div>
              <div data-id="element-95">
                Results are ranked in{' '}
                <b className="text-teal font-bold" data-id="element-96">tiers</b> — full coverage
                first. Each card shows which systems apply and what is missed.
              </div>
            </div>
            <div className="flex gap-2 items-start" data-id="element-97">
              <div className="w-[17px] h-[17px] rounded-full bg-teal text-white text-[0.56rem] font-extrabold flex items-center justify-center shrink-0 mt-0.5" data-id="element-98">
                4
              </div>
              <div data-id="element-99">
                If no full match exists, remove the least acute meridian and
                search again.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="min-h-[300px] md:min-h-[500px]" data-id="element-100">
        {result ? <MatrixResult result={result} selectedMeridians={selectedMeridians} data-id="element-101" /> : <div className="bg-white border border-border rounded-[10px] py-12 md:py-[72px] px-6 md:px-9 text-center shadow-sm" data-id="element-102">
            <span className="text-[3rem] block mb-3.5 opacity-20 grayscale" data-id="element-103">
              ☯
            </span>
            <div className="font-serif text-[1.05rem] text-ink2 mb-2 font-bold" data-id="element-104">
              Select two or more sick meridians
            </div>
            <div className="text-[0.82rem] text-muted italic" data-id="element-105">
              The finder will identify which single meridian best balances all
              of them across Dr. Tan's five balancing systems
            </div>
          </div>}
      </div>
    </div>;
}